<script setup lang="ts">
import { computed, nextTick, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import { useJoakimStore } from '@/stores/useJoakimStore'
import { LoanInformation } from '@/types/JoakimTypes/LoanInformation'

const props = defineProps<{ disable?: boolean }>()

interface SkuldabrefItem {
  innheimtunumer?: string | number
  nafnverd?: string | number
  ved?: Array<{ heimilisfang?: string }>
}
interface SkuldabrefResponse {
  skuldabref?: SkuldabrefItem[]
}

const { t } = useI18n()
const { umsokn, onLoaded } = useUmsokn()
const joakimStore = useJoakimStore()

// ✅ Dynamic validation rules
const rules = computed(() => ({
  umsokn: {
    loanInformation: Array.isArray(umsokn.value?.loanInformation)
      ? umsokn.value.loanInformation.map(() => ({
          loanNumber: { required },
          initialAmount: { required },
          mortgage: { required }
        }))
      : []
  }
}))

const v$ = useVuelidate(rules, { umsokn })

// ✅ Load data, initialize list, and reset validation
onLoaded(async () => {
  if (!umsokn.value) return

  await joakimStore.getSkuldabref()

  const resp = joakimStore.skuldabref as unknown as SkuldabrefResponse | null
  const skuldabrefData = resp?.skuldabref ?? []

  if (
    skuldabrefData.length > 0 &&
    (!umsokn.value.loanInformation || umsokn.value.loanInformation.length === 0)
  ) {
    umsokn.value.loanInformation = skuldabrefData.map((s) => ({
      loanNumber: String(s?.innheimtunumer ?? ''),
      initialAmount: String(s?.nafnverd ?? ''),
      mortgage: String(s?.ved?.[0]?.heimilisfang ?? '')
    }))
  } else if (!umsokn.value.loanInformation || umsokn.value.loanInformation.length === 0) {
    umsokn.value.loanInformation = [{ loanNumber: '', initialAmount: '', mortgage: '' }]
  }

  nextTick(() => v$.value.$reset()) // prevent red errors on load
})

// ✅ Add/remove rows
function addChild() {
  umsokn.value?.loanInformation.push({ loanNumber: '', initialAmount: '', mortgage: '' })
}

function removeChild(index: number) {
  umsokn.value?.loanInformation.splice(index, 1)
  if (umsokn.value.loanInformation.length === 0) addChild()
}

// ✅ Auto-format initialAmount
watch(
  () => umsokn.value.loanInformation,
  (list: LoanInformation[]) => {
    list.forEach((el: LoanInformation) => {
      if (el?.initialAmount) {
        el.initialAmount = el.initialAmount
          .replace(/\D/g, '')
          .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      }
    })
  },
  { deep: true }
)

// ✅ Expose validation
defineExpose({
  async validate() {
    if (Array.isArray(umsokn.value.loanInformation)) {
      umsokn.value.loanInformation.forEach((_: LoanInformation, i: number) => {
        v$.value.umsokn.loanInformation[i].$touch()
      })
    }
    const isValid = await v$.value.$validate()
    return !v$.value.$invalid && isValid
  }
})
</script>

<template>
  <div v-if="umsokn">
    <v-col>
      <div class="forms__header">
        {{ t('lan_nafnabeyting_umsokn_step3_header') }}
      </div>

      <transition-group name="list">
        <div
          v-for="(item, index) in umsokn.loanInformation"
          :key="'loanInformation_' + index"
          class="list-item"
        >
          <div v-if="index > 0" class="forms__divider__container">
            <div class="forms__divider"></div>
            <button
              v-if="!props.disable"
              type="button"
              class="forms__warning__list__wrapper"
              @click="removeChild(index)"
            >
              <img class="forms__warning__icon" src="@/assets/icons/Close.svg" alt="Remove" />
            </button>
          </div>

          <v-row class="animated__label">
            <v-col md="4" cols="12">
              <v-text-field
                v-model="item.loanNumber"
                :readonly="props.disable"
                :label="t('lan_nafnabeyting_umsokn_step3_lansnumer')"
                variant="outlined"
                density="comfortable"
                :error="v$.umsokn.loanInformation[index]?.loanNumber?.$error"
                :error-messages="v$.umsokn.loanInformation[index]?.loanNumber?.$dirty
                  ? [
                      v$.umsokn.loanInformation[index]?.loanNumber?.required?.$invalid
                        ? t('umsokn_general_required_error')
                        : ''
                    ].filter(Boolean)
                  : []"
              />
            </v-col>

            <v-col md="4" cols="12">
              <v-text-field
                v-model="item.initialAmount"
                :readonly="props.disable"
                :label="t('lan_nafnabeyting_umsokn_step3_fjarhaed')"
                variant="outlined"
                density="comfortable"
                :error="v$.umsokn.loanInformation[index]?.initialAmount?.$error"
                :error-messages="v$.umsokn.loanInformation[index]?.initialAmount?.$dirty
                  ? [
                      v$.umsokn.loanInformation[index]?.initialAmount?.required?.$invalid
                        ? t('umsokn_general_required_error')
                        : ''
                    ].filter(Boolean)
                  : []"
              />
            </v-col>

            <v-col md="4" cols="12">
              <v-text-field
                v-model="item.mortgage"
                :readonly="props.disable"
                :label="t('lan_nafnabeyting_umsokn_step3_vedstadur')"
                variant="outlined"
                density="comfortable"
                :error="v$.umsokn.loanInformation[index]?.mortgage?.$error"
                :error-messages="v$.umsokn.loanInformation[index]?.mortgage?.$dirty
                  ? [
                      v$.umsokn.loanInformation[index]?.mortgage?.required?.$invalid
                        ? t('umsokn_general_required_error')
                        : ''
                    ].filter(Boolean)
                  : []"
              />
            </v-col>
          </v-row>
        </div>
      </transition-group>

      <div class="m-t-40" v-if="!props.disable">
        <v-btn
          color="primary"
          variant="outlined"
          prepend-icon="mdi-plus"
          class="mt-4"
          @click="addChild"
        >
          {{ $t('ororku_umsokn_step3_add') }}
        </v-btn>
      </div>
    </v-col>
  </div>
</template>

<style scoped lang="scss">
.forms__divider__container {
  display: flex;
  justify-content: flex-end;
  height: 50px;
}
</style>

<style scoped lang="scss">
.forms__divider__container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 12px;
  position: relative;
}

.forms__divider {
  flex: 1;
  height: 1px;
  background-color: #ddd;
  margin-right: 8px;
}

.forms__warning__list__wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background-color: transparent;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }
}

.forms__warning__icon {
  width: 16px;
  height: 16px;
  pointer-events: none; // ensures the click is on the button, not the img
}


</style>
